/**
 * Steps of the sign-up form for the website measurement (Engagement Center).
 *
 * Same endpoint and rules as the website's attribution.js: POST /web/events via sendBeacon,
 * no visitor id, nothing stored in the browser. The ad origin comes from the attribution the
 * page already read from its link, so a sign-up attempt can be counted per campaign and
 * keyword — including the attempts that never become an account.
 */
import config from 'config';
import type { Attribution } from './attribution';

export type SignupStep = 'register_view' | 'register_start' | 'register_submit' | 'register_error' | 'register_success';

/** Steps on the app's entry page (/welcome): see web-event.model.ts in the API for each field. */
export type EntryStep = 'chat_view' | 'chat_message' | 'chat_card' | 'auth_open' | 'login_success' | 'login_error';

/** Hinweis „SAIA als App installieren“ im Dashboard (Handy). */
export type PwaStep = 'pwa_prompt_view' | 'pwa_prompt_install' | 'pwa_prompt_dismiss' | 'pwa_installed';

/** Optional detail of an event: the entry the page showed, a place or kind, a count, a button. */
export interface WebEventDetail {
  section?: string;
  depth?: number;
  cta?: string;
}

/**
 * Where a refused sign-up failed. Never the entered value, only the place. 'google' marks the
 * steps of a sign-up through "Mit Google anmelden" (submit = click, success = account created).
 */
export type SignupErrorField = 'email' | 'password' | 'exists' | 'server' | 'google';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'gbraid', 'wbraid', 'fbclid'] as const;

export const buildSignupEvent = (
  type: SignupStep | EntryStep | PwaStep,
  path: string,
  attribution?: Attribution,
  field?: SignupErrorField | string,
  detail: WebEventDetail = {},
) => {
  const utm: Partial<Record<(typeof UTM_KEYS)[number], string>> = {};
  for (const key of UTM_KEYS) if (attribution?.[key]) utm[key] = attribution[key];
  return {
    type,
    path,
    utm,
    ...(attribution?.lp ? { landingPath: attribution.lp } : {}),
    ...(attribution?.exp ? { exp: attribution.exp } : {}),
    ...(attribution?.ref ? { referrerHost: attribution.ref } : {}),
    ...(field ? { field } : {}),
    ...(detail.section ? { section: detail.section } : {}),
    ...(detail.depth !== undefined ? { depth: detail.depth } : {}),
    ...(detail.cta ? { cta: detail.cta } : {}),
  };
};

export const sendSignupEvent = (
  type: SignupStep | EntryStep | PwaStep,
  attribution?: Attribution,
  field?: SignupErrorField | string,
  detail?: WebEventDetail,
): void => {
  try {
    const url = `${(config as { apiBaseUrl?: string }).apiBaseUrl || '/api/v1'}/web/events`;
    const data = JSON.stringify(buildSignupEvent(type, window.location.pathname, attribution, field, detail));
    // text/plain: no CORS preflight, and the beacon survives the redirect after a successful sign-up.
    if (navigator.sendBeacon?.(url, new Blob([data], { type: 'text/plain' }))) return;
    void fetch(url, { method: 'POST', body: data, keepalive: true, credentials: 'omit', headers: { 'Content-Type': 'text/plain' } }).catch(() => undefined);
  } catch {
    // Measurement must never get in the way of a sign-up.
  }
};
